/**
 * @page Setup2FAPage
 * @description 2FA TOTP 設定頁面
 * 流程：取得 QR Code → 掃碼 → 輸入驗證碼 → 顯示備用碼
 */

import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/api';
import { useSiteName } from '../../hooks/useSiteName';
import { useAuthStore } from '../../store/auth.store';

// ============================================================
// Types
// ============================================================
interface SetupTotpResponse {
  qrCodeDataUrl: string;
  secret: string;
}

interface EnableTotpResponse {
  backupCodes: string[];
}

// ============================================================
// Schema
// ============================================================
function createVerifySchema(t: (key: string) => string) {
  return z.object({
    totpCode: z
      .string()
      .length(6, t('totpCodeRequired'))
      .regex(/^\d{6}$/, t('totpCodeDigits')),
  });
}

type VerifyFormData = z.infer<ReturnType<typeof createVerifySchema>>;

// ============================================================
// API helpers
// ============================================================
async function fetchSetupTotp({ signal }: { signal: AbortSignal }): Promise<SetupTotpResponse> {
  const res = await apiClient.post<{ data: SetupTotpResponse }>('/auth/2fa/setup', undefined, {
    signal,
  });
  return res.data.data;
}

async function enableTotp(totpCode: string): Promise<EnableTotpResponse> {
  const res = await apiClient.post<{ data: EnableTotpResponse }>('/auth/2fa/enable', { totpCode });
  return res.data.data;
}

// ============================================================
// Component
// ============================================================
export default function Setup2FAPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const siteName = useSiteName();
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((state) => state.accessToken);
  const [isEnabling, setIsEnabling] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  // Step 1: 取得 QR Code
  const {
    data: setupData,
    isLoading: isSetupLoading,
    error: setupError,
  } = useQuery({
    queryKey: ['2fa-setup'],
    queryFn: fetchSetupTotp,
    retry: false,
    enabled: Boolean(accessToken) && !backupCodes,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<VerifyFormData>({
    resolver: zodResolver(createVerifySchema(t)),
  });

  useEffect(() => {
    if (!accessToken && !backupCodes) {
      queryClient.removeQueries({ queryKey: ['2fa-setup'] });
      navigate('/login', { replace: true });
    }
  }, [accessToken, backupCodes, navigate, queryClient]);

  // Keep one-time codes and the submitted TOTP out of the mutation cache.
  const handleEnable = async (data: VerifyFormData) => {
    if (isEnabling || !accessToken || backupCodes) return;
    const revision = useAuthStore.getState().sessionRevision;
    setIsEnabling(true);
    try {
      const result = await enableTotp(data.totpCode);
      if (useAuthStore.getState().sessionRevision !== revision) return;
      reset({ totpCode: '' });
      // Commit the one-time codes before the external auth store notifies subscribers.
      flushSync(() => setBackupCodes(result.backupCodes));
      useAuthStore.getState().clearAuth();
      queryClient.removeQueries({ queryKey: ['2fa-setup'] });
    } catch {
      if (useAuthStore.getState().sessionRevision === revision) {
        setError('totpCode', { message: t('totpVerifyError') });
      }
    } finally {
      setIsEnabling(false);
    }
  };

  const handleDone = () => {
    navigate('/login', { replace: true });
  };

  if (!accessToken && !backupCodes) return null;

  if (!backupCodes && isSetupLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100">
        <div className="text-base-content/60">{t('loadingText')}</div>
      </div>
    );
  }

  if (!backupCodes && setupError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100">
        <div className="text-error text-sm">{t('setupLoadError')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-100">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-8">
          <h1 className="heading-font text-3xl font-bold text-primary">{siteName}</h1>
          <p className="text-base-content/60 mt-2 text-sm">{t('setup2faTitle')}</p>
        </div>

        <div className="bg-base-200 rounded-2xl p-8 shadow-lg border border-base-300">
          {backupCodes ? (
            /* ── 步驟三：顯示備用碼 ── */
            <div>
              <h2 className="text-xl font-semibold text-base-content mb-2">{t('twoFaEnabled')}</h2>
              <p className="text-sm text-base-content/60 mb-5">{t('backupCodesInfo')}</p>
              <p className="text-sm text-base-content/60 mb-5">{t('twoFaReauthenticate')}</p>
              <div className="grid grid-cols-2 gap-2 mb-6">
                {backupCodes.map((code) => (
                  <div
                    key={code}
                    className="bg-base-300 rounded-lg px-3 py-2 font-mono text-sm text-center tracking-widest select-all"
                  >
                    {code}
                  </div>
                ))}
              </div>
              <button
                onClick={handleDone}
                className="w-full py-3 rounded-lg bg-primary text-primary-content font-semibold hover:opacity-90 transition"
              >
                {t('backupCodesDone')}
              </button>
            </div>
          ) : (
            /* ── 步驟一、二：掃碼 + 驗證 ── */
            <div>
              <h2 className="text-xl font-semibold text-base-content mb-2">{t('scanQrCode')}</h2>
              <p className="text-sm text-base-content/60 mb-5">{t('scanQrCodeInfo')}</p>

              {/* QR Code */}
              {setupData?.qrCodeDataUrl && (
                <div className="flex justify-center mb-5">
                  <div className="bg-white p-3 rounded-xl inline-block">
                    <img src={setupData.qrCodeDataUrl} alt="TOTP QR Code" className="w-44 h-44" />
                  </div>
                </div>
              )}

              {/* Secret Key（備用手動輸入） */}
              {setupData?.secret && (
                <div className="mb-5">
                  <p className="text-xs text-base-content/50 mb-1">{t('manualKeyHint')}</p>
                  <div className="bg-base-300 rounded-lg px-3 py-2 font-mono text-xs text-center tracking-widest select-all break-all">
                    {setupData.secret}
                  </div>
                </div>
              )}

              {/* Verify Form */}
              <form onSubmit={handleSubmit(handleEnable)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-base-content/80 mb-1.5">
                    {t('enterCode')}
                  </label>
                  <input
                    {...register('totpCode')}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    className="w-full px-4 py-3 rounded-lg bg-base-300 border border-base-300 text-base-content focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary transition tracking-widest text-center text-lg timer-text"
                    placeholder="000000"
                  />
                  {errors.totpCode && (
                    <p className="mt-1 text-sm text-error">{errors.totpCode.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isEnabling}
                  className="w-full py-3 rounded-lg bg-primary text-primary-content font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isEnabling ? t('verifying') : t('verifyAndEnable')}
                </button>
              </form>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-base-content/30 mt-6">{t('version')}</p>
      </div>
    </div>
  );
}
